import { useCallback, useEffect, useState } from "react";
import { Table, Button, Form, Pagination, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faCopy, faEye, faEdit } from "@fortawesome/free-solid-svg-icons";
import useAxiosPrivate from "../../../Pages/hooks/useAxiosPrivate";
import { baseURL } from "../../../API/API";
import { toast } from "react-toastify";

const MAX_TEACHER_IMAGE_BYTES = 1800 * 1024;

async function prepareTeacherImage(file) {
  if (!file || file.size <= MAX_TEACHER_IMAGE_BYTES) {
    return file;
  }

  const imageUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageUrl;
    });

    const maxSide = 1200;
    const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));

    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (!blob) {
      throw new Error("Image compression failed");
    }

    const safeName = file.name.replace(/\.[^.]+$/, "") || "teacher-image";
    return new File([blob], `${safeName}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const axiosPrivate = useAxiosPrivate();

  // Pagination & Filters
  const [indexpage, setindexPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCredsModal, setShowCredsModal] = useState(false);
  const [generatedCreds, setGeneratedCreds] = useState(null);

  // View modal state
  const [selectedTeacherForView, setSelectedTeacherForView] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Edit modal state
  const [selectedTeacherForEdit, setSelectedTeacherForEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Form Fields (Add)
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [vodafoneCash, setVodafoneCash] = useState("");
  const [instaPay, setInstaPay] = useState("");
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form Fields (Edit)
  const [editName, setEditName] = useState("");
  const [editSubject, setEditSubject] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editVodafoneCash, setEditVodafoneCash] = useState("");
  const [editInstaPay, setEditInstaPay] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPrivate.post(`${baseURL}/learning/teachers-dashboard`, {
        indexpage,
        Search: search,
      });
      setTeachers(res.data?.data || []);
      setTotalPages(res.data?.totalPages || 1);
    } catch (err) {
      console.error("Error fetching teachers:", err);
      toast.error("حدث خطأ أثناء تحميل قائمة المدرسين");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, indexpage, search]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const handleAddImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const preparedFile = await prepareTeacherImage(file);
      setImage(preparedFile);
      if (preparedFile.size < file.size) {
        toast.info("تم ضغط الصورة تلقائيًا لتسهيل رفعها");
      }
    } catch (error) {
      console.error("Error preparing teacher image:", error);
      setImage(null);
      toast.error("تعذر تجهيز الصورة، جرّب صورة أخرى أو حجم أصغر");
      e.target.value = "";
    }
  };

  const handleEditImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const preparedFile = await prepareTeacherImage(file);
      setEditImage(preparedFile);
      setEditImagePreview(URL.createObjectURL(preparedFile));
      if (preparedFile.size < file.size) {
        toast.info("تم ضغط الصورة تلقائيًا لتسهيل رفعها");
      }
    } catch (error) {
      console.error("Error preparing teacher image:", error);
      setEditImage(null);
      toast.error("تعذر تجهيز الصورة، جرّب صورة أخرى أو حجم أصغر");
      e.target.value = "";
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المدرس؟ سيتم حذف حسابه وكافة بياناته المرتبطة به.")) {
      return;
    }
    try {
      await axiosPrivate.delete(`${baseURL}/learning/delete-teacher`, {
        data: { ids: [id] }
      });
      toast.success("تم حذف المدرس بنجاح");
      fetchTeachers();
    } catch (err) {
      console.error("Error deleting teacher:", err);
      toast.error("حدث خطأ أثناء حذف المدرس");
    }
  };

  const handleAddTeacherSubmit = async (e) => {
    e.preventDefault();
    if (!name || !subject) {
      toast.error("الرجاء إدخال الاسم والمادة الدراسية");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("subject", subject);
      formData.append("description", description);
      formData.append("vodafone_cash", vodafoneCash);
      formData.append("instapay", instaPay);
      if (image) {
        formData.append("image", image);
      }

      const res = await axiosPrivate.post(`${baseURL}/learning/insert-teacher`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data?.credentials) {
        setGeneratedCreds(res.data.credentials);
        setShowCredsModal(true);
      }

      toast.success("تم إضافة المدرس وتوليد الحساب بنجاح");
      setShowAddModal(false);
      // Reset form
      setName("");
      setSubject("");
      setDescription("");
      setVodafoneCash("");
      setInstaPay("");
      setImage(null);
      fetchTeachers();
    } catch (err) {
      console.error("Error inserting teacher:", err);
      toast.error(err.response?.data?.message || "حدث خطأ أثناء إضافة المدرس");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (teacher) => {
    setSelectedTeacherForEdit(teacher);
    setEditName(teacher.name);
    setEditSubject(teacher.subject);
    setEditDescription(teacher.description || "");
    setEditVodafoneCash(teacher.vodafone_cash || "");
    setEditInstaPay(teacher.instapay || "");
    setEditEmail(teacher.email || "");
    setEditPassword("");
    setEditImage(null);
    setEditImagePreview(teacher.profile_image || null);
    setShowEditModal(true);
  };

  const handleEditTeacherSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("id", selectedTeacherForEdit.id);
      formData.append("name", editName);
      formData.append("subject", editSubject);
      formData.append("description", editDescription);
      formData.append("vodafone_cash", editVodafoneCash);
      formData.append("instapay", editInstaPay);
      formData.append("email", editEmail);
      if (editPassword) {
        formData.append("password", editPassword);
      }
      if (editImage) {
        formData.append("image", editImage);
      }

      await axiosPrivate.post(`${baseURL}/learning/edit-teacher`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("تم تعديل بيانات المدرس بنجاح");
      setShowEditModal(false);
      fetchTeachers();
    } catch (err) {
      console.error("Error editing teacher:", err);
      toast.error(err.response?.data?.message || "حدث خطأ أثناء تعديل المعلم");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewClick = (teacher) => {
    setSelectedTeacherForView(teacher);
    setShowViewModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("تم النسخ للحافظة");
  };

  return (
    <div className="p-4" dir="rtl">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h2 style={{ fontWeight: 800 }}>إدارة المدرسين</h2>
        <Button variant="primary" onClick={() => setShowAddModal(true)} className="d-flex align-items-center gap-2" style={{ backgroundColor: "var(--brand-purple)", borderColor: "var(--brand-purple)" }}>
          <FontAwesomeIcon icon={faPlus} />
          إضافة مدرس جديد
        </Button>
      </div>

      {/* شريط البحث */}
      <div className="mb-4">
        <Form.Control
          type="text"
          placeholder="ابحث عن مدرس بالاسم أو البريد..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: "400px", borderRadius: "10px" }}
        />
      </div>

      {/* جدول البيانات */}
      {loading ? (
        <div className="text-center py-5">جاري التحميل...</div>
      ) : teachers.length > 0 ? (
        <div className="table-responsive shadow-sm" style={{ borderRadius: "12px", overflow: "hidden" }}>
          <Table striped bordered hover className="align-middle mb-0">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "80px" }}>الصورة</th>
                <th>الاسم</th>
                <th>المادة</th>
                <th>البريد الإلكتروني</th>
                <th>نبذة عن المدرس</th>
                <th style={{ width: "160px" }} className="text-center">العمليات</th>
              </tr>
            </thead>
            <tbody>
              {teachers.map((teacher) => (
                <tr key={teacher.id}>
                  <td>
                    <img
                      src={teacher.profile_image || "https://cdn-icons-png.flaticon.com/512/194/194931.png"}
                      alt={teacher.name}
                      style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover" }}
                    />
                  </td>
                  <td style={{ fontWeight: "bold" }}>{teacher.name}</td>
                  <td>
                    <span className="badge bg-primary" style={{ backgroundColor: "var(--brand-purple)" }}>{teacher.subject}</span>
                  </td>
                  <td>{teacher.email}</td>
                  <td style={{ maxWidth: "300px" }} className="text-truncate">{teacher.description || "-"}</td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center gap-2">
                      <Button variant="outline-primary" size="sm" onClick={() => handleViewClick(teacher)} title="عرض التفاصيل">
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button variant="outline-success" size="sm" onClick={() => handleEditClick(teacher)} title="تعديل">
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(teacher.id)} title="حذف">
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">لا يوجد مدرسين مضافين حالياً.</div>
      )}

      {/* الترقيم */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={idx + 1 === indexpage}
                onClick={() => setindexPage(idx + 1)}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </div>
      )}

      {/* Modal إضافة مدرس */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered dir="rtl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: "bold" }}>إضافة مدرس وتوليد الحساب</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddTeacherSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>الاسم بالكامل</Form.Label>
              <Form.Control
                type="text"
                placeholder="مثال: د/ محمد أيمن"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>المادة الدراسية / التخصص</Form.Label>
              <Form.Control
                type="text"
                placeholder="مثال: الأحياء والعلوم المتكاملة"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>نبذة تعريفية عن المدرس</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="اكتب نبذة تعريفية تظهر في صفحة المدرس..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>رقم فودافون كاش (اختياري)</Form.Label>
              <Form.Control
                type="text"
                placeholder="مثال: 01012345678"
                value={vodafoneCash}
                onChange={(e) => setVodafoneCash(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>عنوان انستا باي (اختياري)</Form.Label>
              <Form.Control
                type="text"
                placeholder="مثال: user@instapay"
                value={instaPay}
                onChange={(e) => setInstaPay(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>الصورة الشخصية</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleAddImageChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              إلغاء
            </Button>
            <Button variant="primary" type="submit" disabled={submitting} style={{ backgroundColor: "var(--brand-purple)", borderColor: "var(--brand-purple)" }}>
              {submitting ? "جاري الحفظ وتوليد الحساب..." : "حفظ الحساب"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal عرض بيانات المدرس بالكامل */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered dir="rtl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: "bold" }}>بيانات المعلم بالكامل</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedTeacherForView && (
            <div className="d-flex flex-column gap-3">
              <div className="text-center mb-3">
                <img
                  src={selectedTeacherForView.profile_image || "https://cdn-icons-png.flaticon.com/512/194/194931.png"}
                  alt={selectedTeacherForView.name}
                  style={{ width: "120px", height: "120px", borderRadius: "50%", objectFit: "cover", border: "3px solid var(--brand-purple)" }}
                />
              </div>
              <div>
                <strong>الاسم بالكامل:</strong>
                <p className="bg-light p-2 mt-1 rounded-3 fw-bold">{selectedTeacherForView.name}</p>
              </div>
              <div>
                <strong>المادة الدراسية:</strong>
                <p className="bg-light p-2 mt-1 rounded-3">{selectedTeacherForView.subject}</p>
              </div>
              <div>
                <strong>البريد الإلكتروني:</strong>
                <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded-3 mt-1">
                  <code>{selectedTeacherForView.email}</code>
                  <Button size="sm" variant="outline-secondary" onClick={() => copyToClipboard(selectedTeacherForView.email)}>
                    <FontAwesomeIcon icon={faCopy} />
                  </Button>
                </div>
              </div>
              <div>
                <strong>كلمة المرور الافتراضية:</strong>
                <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded-3 mt-1">
                  <code>{selectedTeacherForView.raw_password || "غير مسجلة"}</code>
                  {selectedTeacherForView.raw_password && (
                    <Button size="sm" variant="outline-secondary" onClick={() => copyToClipboard(selectedTeacherForView.raw_password)}>
                      <FontAwesomeIcon icon={faCopy} />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <strong>رقم فودافون كاش:</strong>
                <p className="bg-light p-2 mt-1 rounded-3">{selectedTeacherForView.vodafone_cash || "لا يوجد"}</p>
              </div>
              <div>
                <strong>عنوان انستا باي:</strong>
                <p className="bg-light p-2 mt-1 rounded-3">{selectedTeacherForView.instapay || "لا يوجد"}</p>
              </div>
              <div>
                <strong>نبذة تعريفية:</strong>
                <p className="bg-light p-2 mt-1 rounded-3" style={{ whiteSpace: "pre-wrap" }}>{selectedTeacherForView.description || "لا يوجد"}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)} className="w-100">
            إغلاق
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal تعديل مدرس */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered dir="rtl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: "bold" }}>تعديل بيانات المعلم</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditTeacherSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>الاسم بالكامل</Form.Label>
              <Form.Control
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>المادة الدراسية / التخصص</Form.Label>
              <Form.Control
                type="text"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>نبذة تعريفية عن المدرس</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>رقم فودافون كاش (اختياري)</Form.Label>
              <Form.Control
                type="text"
                value={editVodafoneCash}
                onChange={(e) => setEditVodafoneCash(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>عنوان انستا باي (اختياري)</Form.Label>
              <Form.Control
                type="text"
                value={editInstaPay}
                onChange={(e) => setEditInstaPay(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>البريد الإلكتروني للمدرس</Form.Label>
              <Form.Control
                type="email"
                name="teacher_email"
                autoComplete="new-email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>تغيير كلمة المرور (اتركه فارغاً إذا كنت لا تريد التغيير)</Form.Label>
              <Form.Control
                type="password"
                name="teacher_password"
                autoComplete="new-password"
                placeholder="أدخل كلمة المرور الجديدة"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>الصورة الشخصية الجديدة</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleEditImageChange}
              />
              {editImagePreview && (
                <div className="mt-3 text-center">
                  <img
                    src={editImagePreview}
                    alt="Preview"
                    style={{ width: "90px", height: "90px", borderRadius: "50%", objectFit: "cover" }}
                  />
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              إلغاء
            </Button>
            <Button variant="primary" type="submit" disabled={submitting} style={{ backgroundColor: "var(--brand-purple)", borderColor: "var(--brand-purple)" }}>
              {submitting ? "جاري الحفظ..." : "حفظ التعديلات"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal عرض بيانات الدخول عند الإنشاء */}
      <Modal show={showCredsModal} onHide={() => setShowCredsModal(false)} centered dir="rtl">
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title style={{ fontWeight: "bold" }}>تم توليد حساب المدرس بنجاح!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center">
          <p className="mb-4">يرجى حفظ بيانات الدخول التالية وإرسالها للمدرس:</p>
          {generatedCreds && (
            <div className="p-3 bg-light rounded text-start mb-3" style={{ border: "1px dashed #28a745" }}>
              <div className="mb-3">
                <strong>البريد الإلكتروني:</strong>
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <code>{generatedCreds.email}</code>
                  <Button size="sm" variant="outline-secondary" onClick={() => copyToClipboard(generatedCreds.email)}>
                    <FontAwesomeIcon icon={faCopy} />
                  </Button>
                </div>
              </div>
              <div>
                <strong>كلمة المرور الافتراضية:</strong>
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <code>{generatedCreds.password}</code>
                  <Button size="sm" variant="outline-secondary" onClick={() => copyToClipboard(generatedCreds.password)}>
                    <FontAwesomeIcon icon={faCopy} />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <p className="text-danger small">ملاحظة: بمجرد إغلاق هذه النافذة فلن تتمكن من رؤية كلمة المرور مرة أخرى.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setShowCredsModal(false)} className="w-100">
            حسناً، قمت بحفظ البيانات
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
